import {
  dehydrate,
  hydrate,
  type DehydratedState,
  type Query,
  type QueryCacheNotifyEvent,
  type QueryClient,
  type QueryKey,
} from "@tanstack/react-query";

const RESUME_CACHE_VERSION = 1;
const STORAGE_PREFIX = "stasht_resume_query_cache";
const MAX_CACHE_AGE_MS = 24 * 60 * 60 * 1000;
const MAX_QUERY_AGE_MS = 24 * 60 * 60 * 1000;
const MAX_STORAGE_BYTES = 1_250_000;
const MAX_STORED_QUERIES = 180;
const PERSIST_DEBOUNCE_MS = 750;
const PERSIST_IDLE_TIMEOUT_MS = 5000;

// Keep the durable resume snapshot focused on cold-open experience.
// Home is the authenticated app entry point, so prefer its shell/feed data over
// detail, map, or deep-link caches that can crowd out the startup snapshot.
const RESUME_QUERY_ROOTS = new Set([
  "profile",
  "user-tags",
  "tag-stats",
  "shelves",
  "shelf-posts",
  "home-rails",
  "stashes",
  "events",
]);

interface StoredResumeQueryCache {
  version: number;
  userId: string;
  savedAt: number;
  state: DehydratedState;
}

type MutableDehydratedState = DehydratedState & {
  queries: Array<DehydratedState["queries"][number]>;
};

const now = () => Date.now();

const getStorage = () => {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

export const getResumeQueryCacheStorageKey = (userId: string) =>
  `${STORAGE_PREFIX}_v${RESUME_CACHE_VERSION}:${encodeURIComponent(userId)}`;

const queryRoot = (queryKey: QueryKey) => {
  const root = queryKey[0];
  return typeof root === "string" ? root : null;
};

const isUnsafeResumeQueryKey = (queryKey: QueryKey) => {
  const root = queryRoot(queryKey);
  if (!root) return false;

  if (root === "entity-stash-edges") {
    return true;
  }

  return root === "entity" && queryKey[2] === "connected";
};

export const isResumeQueryKey = (queryKey: QueryKey) => {
  const root = queryRoot(queryKey);
  if (!root) return false;
  if (isUnsafeResumeQueryKey(queryKey)) return false;

  // Recent stash ID pages live with the item units in IndexedDB. Excluding
  // both the new key and historical recent-feed keys prevents localStorage
  // from restoring a second copy with stale pagination internals.
  if (root === "stashes" && queryKey[1] === "recent") return false;

  // Home's Favorites/Done covers and counts come from the account catalog.
  // Restore only that query: collection detail/member/invite data stays out.
  if (root === "collections") {
    return queryKey.length === 2 && queryKey[1] === "personal";
  }

  // Home's Roundups cover and count. Whole editions stay out.
  if (root === "roundups") {
    return queryKey.length === 2 && (queryKey[1] === "list" || queryKey[1] === "activity");
  }

  if (root === "search") {
    const searchText = queryKey[1];
    return typeof searchText === "string" && searchText.trim().length > 0;
  }

  return RESUME_QUERY_ROOTS.has(root);
};

export const isResumeQuery = (query: Query) => {
  if (!isResumeQueryKey(query.queryKey)) return false;
  if (query.state.status !== "success") return false;
  if (query.state.data === undefined) return false;
  if (query.state.dataUpdatedAt <= 0) return false;
  return now() - query.state.dataUpdatedAt <= MAX_QUERY_AGE_MS;
};

const sortQueriesNewestFirst = (state: DehydratedState): MutableDehydratedState => ({
  mutations: [],
  queries: [...state.queries]
    .sort((a, b) => b.state.dataUpdatedAt - a.state.dataUpdatedAt)
    .slice(0, MAX_STORED_QUERIES),
});

const serializeWithByteBudget = (payload: StoredResumeQueryCache) => {
  let next: StoredResumeQueryCache = {
    ...payload,
    state: sortQueriesNewestFirst(payload.state),
  };

  while (next.state.queries.length > 0) {
    const serialized = JSON.stringify(next);
    if (serialized.length <= MAX_STORAGE_BYTES) return serialized;
    next = {
      ...next,
      state: {
        mutations: [],
        queries: next.state.queries.slice(0, Math.max(0, next.state.queries.length - 1)),
      },
    };
  }

  return null;
};

export const restoreResumeQueryCache = (queryClient: QueryClient, userId: string) => {
  const storage = getStorage();
  if (!storage) return false;

  const key = getResumeQueryCacheStorageKey(userId);
  const raw = storage.getItem(key);
  if (!raw) return false;

  try {
    const payload = JSON.parse(raw) as StoredResumeQueryCache;
    if (payload.version !== RESUME_CACHE_VERSION || payload.userId !== userId) {
      storage.removeItem(key);
      return false;
    }
    if (!payload.state?.queries || now() - payload.savedAt > MAX_CACHE_AGE_MS) {
      storage.removeItem(key);
      return false;
    }

    const filteredState: DehydratedState = {
      mutations: [],
      queries: payload.state.queries.filter(query => isResumeQueryKey(query.queryKey)),
    };

    if (filteredState.queries.length === 0) {
      storage.removeItem(key);
      return false;
    }

    hydrate(queryClient, filteredState);
    return true;
  } catch {
    storage.removeItem(key);
    return false;
  }
};

export const persistResumeQueryCache = (queryClient: QueryClient, userId: string) => {
  const storage = getStorage();
  if (!storage) return false;

  const key = getResumeQueryCacheStorageKey(userId);
  const state = dehydrate(queryClient, {
    shouldDehydrateQuery: isResumeQuery,
    shouldDehydrateMutation: () => false,
  });

  if (state.queries.length === 0) {
    storage.removeItem(key);
    return false;
  }

  const serialized = serializeWithByteBudget({
    version: RESUME_CACHE_VERSION,
    userId,
    savedAt: now(),
    state,
  });

  if (!serialized) {
    storage.removeItem(key);
    return false;
  }

  try {
    storage.setItem(key, serialized);
    return true;
  } catch {
    storage.removeItem(key);
    return false;
  }
};

export const clearResumeQueryCache = (userId?: string | null) => {
  const storage = getStorage();
  if (!storage || !userId) return;
  storage.removeItem(getResumeQueryCacheStorageKey(userId));
};

export const refreshActiveResumeQueries = (queryClient: QueryClient) =>
  queryClient.invalidateQueries({
    predicate: query => isResumeQueryKey(query.queryKey),
    refetchType: "active",
  });

const shouldSchedulePersist = (event: QueryCacheNotifyEvent) => {
  if (!("query" in event)) return false;
  if (!isResumeQueryKey(event.query.queryKey)) return false;
  if (event.type === "removed") return true;
  if (event.type !== "updated") return false;
  return event.action.type === "success";
};

export const installResumeQueryCachePersistence = (queryClient: QueryClient, userId: string) => {
  let persistTimer: number | undefined;
  let idleCallbackId: number | undefined;

  const cancelScheduledPersist = () => {
    if (persistTimer !== undefined) {
      window.clearTimeout(persistTimer);
      persistTimer = undefined;
    }
    if (idleCallbackId !== undefined) {
      window.cancelIdleCallback?.(idleCallbackId);
      idleCallbackId = undefined;
    }
  };

  const persistNow = () => {
    cancelScheduledPersist();
    persistResumeQueryCache(queryClient, userId);
  };

  const persistWhenIdle = () => {
    idleCallbackId = undefined;
    persistResumeQueryCache(queryClient, userId);
  };

  const schedulePersist = () => {
    if (typeof window === "undefined") return;
    cancelScheduledPersist();
    persistTimer = window.setTimeout(() => {
      persistTimer = undefined;
      if (document.visibilityState === "hidden" || !window.requestIdleCallback) {
        persistResumeQueryCache(queryClient, userId);
        return;
      }
      idleCallbackId = window.requestIdleCallback(persistWhenIdle, {
        timeout: PERSIST_IDLE_TIMEOUT_MS,
      });
    }, PERSIST_DEBOUNCE_MS);
  };

  const unsubscribe = queryClient.getQueryCache().subscribe(event => {
    if (shouldSchedulePersist(event)) {
      schedulePersist();
    }
  });

  const persistWhenHidden = () => {
    if (document.visibilityState === "hidden") {
      persistNow();
    }
  };

  const persistOnPageHide = () => persistNow();

  document.addEventListener("visibilitychange", persistWhenHidden);
  window.addEventListener("pagehide", persistOnPageHide);
  schedulePersist();

  return () => {
    unsubscribe();
    document.removeEventListener("visibilitychange", persistWhenHidden);
    window.removeEventListener("pagehide", persistOnPageHide);
    persistNow();
  };
};
